/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { noop } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import { Modal } from '@blend/enterprise-components';
import { ActionButton, Text, BrandColors, Header, Li } from '@blend/enterprise-components';
import { ModalContent } from 'blb-components/ModalContent';

const styles = StyleSheet.create({
  border: { border: `1px solid ${BrandColors.CLOUD}` },
  cellPadding: { padding: 5 },
  cellWidth: { width: '40%' },
  center: {
    textAlign: 'center',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  indent: {
    paddingLeft: 16,
  },
  mainTextBody: {
    border: `1px solid ${BrandColors.CLOUD_50}`,
    borderRadius: 4,
    height: 440,
    marginBottom: 16,
    marginTop: 16,
    overflow: 'auto',
    padding: 16,
  },
});

export namespace PrivacyPolicyModal {
  export interface Props {
    handleClose: () => void;
    isOpen: boolean;
  }
}

export const PrivacyPolicyModal: React.FC<PrivacyPolicyModal.Props> = ({ isOpen, handleClose }) => {
  return (
    <Modal contentLabel="Privacy Policy" handleClose={noop} isOpen={isOpen}>
      <ModalContent headerText="Privacy Policy" id="it-PrivacyPolicyModal">
        <div className={css(styles.mainTextBody)}>
          <br />
          <Text weight="bold">
            If you are California resident, please see our Notice at Collection for California Residents.
          </Text>
          <br />
          <br />
          <Text>
            This Privacy Notice (“Privacy Notice”) describes and governs the information collection, use, and sharing
            practices of Blend Labs, Inc. and its subsidiaries, affiliates, and divisions as may change from time to
            time (collectively, “Blend”, “we”, “us”, and “our”) with respect to use of the Blend Workspace platform
            (“Blend Workspace”) and the services we provide through our website and the Blend Workspace platform
            (collectively, the Blend Workspace platform and the services available through them are the “Services”).
          </Text>
          <br />
          <br />
          <Text>
            Before you use or submit any information through or in connection with the Services, please carefully review
            this Privacy Notice. By using any part of the Services, you understand that your information will be
            collected, used, and disclosed as outlined in this Privacy Notice. IF YOU DO NOT AGREE TO THIS PRIVACY
            NOTICE, PLEASE DO NOT USE THE SERVICES.
          </Text>
          <br />
          <br />
          <br />
          <Header size={1}>1. Information We Collect</Header>
          <br />
          <Text>
            We collect information in multiple ways, including when you provide information directly to us; when we
            passively collect information from you, such as from your browser or device; and from third parties. We may
            combine all of the information we collect about you from these various sources.
          </Text>
          <br />
          <br />
          <Text weight="bold">Information You Provide Directly to Us</Text>
          <br />
          <br />
          <Text>
            We will collect any information that you provide to us. For example, if you create an account with us, we
            will collect information such as your name, email address, phone number, mailing address, and other profile
            information. We will also collect information you send through the Services from and about your clients and
            prospective clients. You should never transmit information about your clients and prospective clients
            through the Services without their explicit consent, and you should handle their information in accordance
            with their instructions and applicable privacy laws. If you access the Services through a third-party
            connection or log-in (e.g., through a social network), you may allow us to have access to and store certain
            information from your social network profile. This includes your name, gender, and profile picture,
            depending on your settings on such services. If you do not wish to have this information shared, do not use
            a social networking connection to access the Services. For a description of how social networking sites
            handle your information, please refer to their privacy policies and terms of use, which may permit you to
            modify your privacy settings.
          </Text>
          <br />
          <br />
          <Text weight="bold">Information That is Automatically Collected Device/Usage Information</Text>
          <br />
          <br />
          <Text>
            We may automatically collect certain information about the computer or devices you use to access the
            Services. As described further below, we may collect and analyze information such as (a) IP addresses,
            general location information (e.g., city/state), unique device identifiers, IMEI and TCP/IP address, and
            other information about your device(s), browser types, browser language, operating system, and mobile device
            carrier information; and (b) information related to the ways in which you interact with the Services,
            platform type, the number of clicks, domain names, content viewed, the date and time you used the Services,
            the frequency of your use of the Services, error logs, and other similar information. As described further
            below, we may use third-party analytics providers and technologies, including cookies and similar tools, to
            assist in collecting this information. Please note that we will ask your permission before collecting your
            precise GPS location.
          </Text>
          <br />
          <br />
          <Text weight="bold">Information from Third Parties</Text>
          <br />
          <br />
          <Text>
            We may also collect information about you or others through non-affiliated third parties, including
            partners, marketers, and researchers, and combine such information with other information collected directly
            from you.
          </Text>
          <br />
          <br />
          <Text weight="bold">Cookies and Other Tracking Technologies</Text>
          <br />
          <br />
          <Text>
            We also collect data about your use of the Services through the use of Internet server logs and online
            tracking technologies, like cookies and/or tracking pixels. A web server log is a file where website
            activity is stored. A cookie is a small text file that is placed on your computer when you visit a website,
            that enables us to: (a) recognize your computer or device; (b) store your preferences and settings; (c)
            understand usage trends; (d) enhance your experience by delivering content specific to your inferred
            interests; and (e) assist with security and other administrative functions. As we adopt additional
            technologies, we may also gather additional information through other methods.
          </Text>
          <br />
          <br />
          <Text>
            Please note that you can change your settings to notify you when a cookie is being set or updated, or to
            block cookies altogether. Please consult the "Help" section of your browser for more information (e.g.,
            Internet Explorer; Google Chrome; Mozilla Firefox; or Apple Safari). Please note that by blocking any or all
            cookies, you may not have access to certain features or offerings of the Services.
          </Text>
          <br />
          <br />
          <br />
          <br />
          <Header size={1}>2. How We Use Your Information</Header>
          <br />
          <Text>We may use the information we collect from and about you for the following purposes:</Text>
          <br />
          <ul>
            <Li centerAligned={false}>
              For the purposes for which it is provided, such as to facilitate connections between agents and lenders;
            </Li>
            <Li centerAligned={false}>
              To provide and improve the Services, including to develop new features or services, to take steps to
              secure the Services, and for technical and customer support;
            </Li>
            <Li centerAligned={false}>
              To send you information about your relationship or transactions with us, account alerts, or other similar
              communications;
            </Li>
            <Li centerAligned={false}>
              For marketing and advertising purposes, including to contact you with information or surveys that we
              believe may be of interest to you both regarding our products and Services and those of third parties;
            </Li>
            <Li centerAligned={false}>To process and respond to your inquiries or to request your feedback;</Li>

            <Li centerAligned={false}>
              For analytics, research, and reporting, including to synthesize and derive insights from your use of our
              Services;
            </Li>
            <Li centerAligned={false}>
              For internal administrative purposes, such as taking steps to secure the Services;
            </Li>
            <Li centerAligned={false}>
              To comply with the law and protect the safety, rights, property, or security of Blend, the Services, our
              customers, and the general public; and
            </Li>
            <Li centerAligned={false}>
              To enforce our Terms of Use, including to investigate potential violations thereof.
            </Li>
          </ul>
          <br />
          <br />
          <Text>
            We may aggregate and/or de-identify information collected through the Services. We may use de-identified
            and/or aggregated data for any purpose, including without limitation for research and marketing purposes.
          </Text>
          <br />
          <br />
          <br />
          <Header size={1}>3. When We Disclose Your Information</Header>
          <br />
          <Text>We may disclose and/or share your information under the following circumstances:</Text>
          <br />
          <br />
          <Text>
            <Text weight="bold">Service Providers</Text> We may disclose your information to third parties who perform
            services on our behalf, including, without limitation: marketing, market research, customer support, data
            storage, data analysis and processing, and legal services.
          </Text>
          <br />
          <br />
          <Text>
            <Text weight="bold">Legal Compliance and Protection of Blend and Others</Text> We may disclose your
            information if required to do so by law or on a good faith belief that such disclosure is permitted by this
            Privacy Notice or reasonably necessary or appropriate for any of the following reasons: (a) to comply with
            legal process; (b) to enforce this Privacy Notice, or other contracts with you, including investigation of
            potential violations thereof; (c) to respond to your requests for customer service; and/or (d) to protect
            the rights, property, or personal safety of Blend, our agents and affiliates, our customers, and the public.
            This includes exchanging information with other companies and organizations for fraud protection, and
            spam/malware prevention, and similar purposes.
          </Text>
          <br />
          <br />
          <Text>
            <Text weight="bold">Business Transfers</Text> As we continue to develop our business, we may buy, merge, or
            partner with other companies. In such transactions, (including in contemplation of such transactions, e.g.,
            due diligence) your information may be disclosed. If any of Blend’s assets are sold or transferred to a
            third party, our data (including your contact information) would likely be one of the transferred business
            assets.
          </Text>
          <br />
          <br />
          <Text>
            <Text weight="bold">Affiliated Companies</Text> We may disclose your information with current or future
            affiliated companies.
          </Text>
          <br />
          <br />
          <Text>
            <Text weight="bold">Consent</Text> We may disclose your information to any third parties based on your
            consent to do so, such as when you request that we facilitate a connection and/or exchange of information
            with another party through the Services.
          </Text>
          <br />
          <br />
          <Text>
            <Text weight="bold">Aggregate/De-identified Information</Text> We may disclose de-identified and/or
            aggregated data for any purpose to third parties, including advertisers, promotional partners, and/or
            others.
          </Text>
          <br />
          <br />
          <br />
          <Header size={1}>4. Online Analytics</Header>
          <br />
          <Text>
            We may use web analytics services (such as those of Google Analytics) on our Services to collect and analyze
            the information discussed above and support the uses of information described above. The information
            collected by various analytics technologies described in the “Cookies and Other Tracking Technologies”
            section above will be disclosed to or collected directly by these service providers, who use the information
            to evaluate your use of the Services, including by analyzing usage trends, assisting with fraud prevention,
            and providing certain features to you. To learn more about Google’s privacy practices, please review the
            Google Privacy Policy; to prevent Google Analytics from using your information for analytics, you may
            install the Google Analytics Opt-out Browser Add-on by clicking here.
          </Text>
          <br />
          <br />
          <br />
          <Header size={1}>5. Your Choices and Rights</Header>
          <br />
          <Text>
            If you have registered for an account with us through our Services, you can update certain information and
            your email communications options by logging into your account and updating your account information and
            communications settings. You can unsubscribe from marketing emails by clicking the "unsubscribe" link they
            contain or emailing us at privacy@blendlabs.com with your communications preferences. You may also be able
            to review and update certain personal information by emailing us at privacy@blendlabs.com.
          </Text>
          <br />
          <br />
          <br />
          <Header size={1}>6. California Residents</Header>
          <br />
          <Text>
            This section of this Notice applies solely to those individuals who reside in the State of California
            ("consumers" or "you"). We are providing this Privacy Notice in compliance with the California Consumer
            Privacy Act of 2018 ("CCPA") and other applicable California privacy laws. Any capitalized terms used in
            this section that are not defined in the Notice itself have the same definition as used in the CCPA and its
            implementing regulations.
          </Text>
          <br />
          <br />
          <Text>
            Certain information we collect may be exempt from the CCPA because it is considered public information
            (i.e., it is made available by a government entity) or covered by a specific federal privacy law, such as
            the Gramm–Leach–Bliley Act, the California Financial Information Privacy Act, or the Fair Credit Reporting
            Act.
          </Text>
          <br />
          <br />
          <Text weight="bold">Notice at Collection Regarding the Categories of Personal Information Collected</Text>
          <br />
          <br />
          <Text>
            You have the right to receive notice of the categories of personal information we collect, and the purposes
            for which we use personal information. The categories we use below to describe personal information are
            those enumerated in the CCPA. We collect this personal information for the purposes described in the How We
            Use Your Information section above.
          </Text>
          <table>
            <tbody>
              <tr className={css(styles.border)}>
                <th className={css(styles.border, styles.cellPadding)}>
                  <Header size={6}>Information Type</Header>
                </th>
                <th className={css(styles.border, styles.cellPadding)}>
                  <Header size={6}>Collected</Header>
                </th>
                <th className={css(styles.border, styles.cellPadding)}>
                  <Header size={6}>Disclosed</Header>
                </th>
                <th className={css(styles.border, styles.cellPadding)}>
                  <Header size={6}>Sold</Header>
                </th>
                <th className={css(styles.border, styles.cellPadding)}>
                  <Header size={6}>Recipients</Header>
                </th>
              </tr>
              <tr className={css(styles.border)}>
                <td className={css(styles.border, styles.cellPadding, styles.cellWidth)}>
                  <Text weight="bold">Personal Identifiers:</Text>
                  <ul>
                    <Li centerAligned={false}>
                      Contact information (such as name, phone number, address, email address)
                    </Li>
                    <Li centerAligned={false}>Unique identifiers (such as those assigned in cookies)</Li>
                  </ul>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>Yes</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>Yes</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>No</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <ul>
                    <Li centerAligned={false}>Vendors/Service Providers</Li>
                    <Li centerAligned={false}>Affiliates of Blend</Li>
                    <Li centerAligned={false}>Lenders or other Financial Services Providers</Li>
                  </ul>
                </td>
              </tr>

              <tr className={css(styles.border)}>
                <td className={css(styles.border, styles.cellPadding, styles.cellWidth)}>
                  <Text weight="bold">Geolocation Information:</Text>
                  <ul>
                    <Li centerAligned={false}>Precise (e.g., via IP address)</Li>
                    <Li centerAligned={false}>Coarse (e.g., ZIP code)</Li>
                  </ul>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>Yes</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>Yes</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>No</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <ul>
                    <Li centerAligned={false}>Vendors/Service Providers</Li>
                    <Li centerAligned={false}>Affiliates of Blend</Li>
                    <Li centerAligned={false}>Lenders or otherFinancial Services Providers</Li>
                  </ul>
                </td>
              </tr>
              <tr className={css(styles.border)}>
                <td className={css(styles.border, styles.cellPadding, styles.cellWidth)}>
                  <Text weight="bold">Internet or Electronic Network Activity Information:</Text>
                  <ul>
                    <Li centerAligned={false}>IP address</Li>
                    <Li centerAligned={false}>Device identifier (e.g., MAC)</Li>
                    <Li centerAligned={false}>Information provided in URL string (e.g., search keywords)</Li>
                    <Li centerAligned={false}>Cookie or tracking pixel information</Li>
                    <Li centerAligned={false}>
                      Information about your interaction with our website, app, or email correspondence
                    </Li>
                  </ul>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>Yes</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>Yes</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>No</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <ul>
                    <Li centerAligned={false}>Vendors/Service Providers</Li>
                    <Li centerAligned={false}>Affiliates of Blend</Li>
                    <Li centerAligned={false}>Lenders or other Financial Services Providers</Li>
                  </ul>
                </td>
              </tr>

              <tr className={css(styles.border)}>
                <td className={css(styles.border, styles.cellPadding, styles.cellWidth)}>
                  <Text weight="bold">Audio, Electronic, Visual, Thermal, Olfactory, or Similar Information:</Text>
                  <ul>
                    <Li centerAligned={false}>Call recording (e.g., customer service calls)</Li>
                    <Li centerAligned={false}>Photographs</Li>
                  </ul>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>Yes</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>Yes</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>No</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <ul>
                    <Li centerAligned={false}>Vendors/Service Providers</Li>
                    <Li centerAligned={false}>Affiliates of Blend</Li>
                    <Li centerAligned={false}>Lenders or other Financial Services Providers</Li>
                  </ul>
                </td>
              </tr>
              <tr className={css(styles.border)}>
                <td className={css(styles.border, styles.cellPadding, styles.cellWidth)}>
                  <Text weight="bold">Professional or Employment-Related Information:</Text>
                  <ul>
                    <Li centerAligned={false}>Current employer</Li>
                    <Li centerAligned={false}>Employment history</Li>
                  </ul>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>Yes</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>Yes</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <Text>No</Text>
                </td>
                <td className={css(styles.border, styles.cellPadding)}>
                  <ul>
                    <Li centerAligned={false}>Vendors/Service Providers</Li>
                    <Li centerAligned={false}>Affiliates of Blend</Li>
                    <Li centerAligned={false}>Lenders or other Financial Services Providers</Li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
          <br />
          <br />
          <Text>
            We <Text weight="bold">do not</Text> collect the following categories of information from you:
          </Text>
          <br />
          <ul>
            <Li centerAligned={false}> Education Information</Li>
            <Li centerAligned={false}> Biometric Information</Li>
            <Li centerAligned={false}> Inferences Drawn About You</Li>
            <Li centerAligned={false}> Commercial Information</Li>
            <Li centerAligned={false}> Protected Classifications</Li>
            <Li centerAligned={false}> Payment Information</Li>
            <Li centerAligned={false}> Health Information</Li>
          </ul>
          <br />
          <br />
          <Text weight="bold">Right to Know</Text>
          <br />
          <br />
          <Text>
            California law provides California consumers with the right to request that certain covered businesses
            provide you (i) the categories of personal information we collect, use, disclose or sell about you; (ii) the
            categories of sources of such information; (iii) the business or commercial purpose for collecting or
            selling your personal information (as applicable); (iv) the categories of personal information we sell or
            have sold or disclosed for a business purpose about the consumer and the categories of third parties to whom
            we’ve disclosed your personal information and, (v) specific pieces of personal information that a business
            has collected about the consumer. This “Right to Know” is subject to certain exceptions as provided by law.
          </Text>
          <br />
          <br />
          <Text weight="bold">Right to Delete</Text>
          <br />
          <br />
          <Text>
            California law provides California consumers with the right to request that certain covered businesses
            delete personal information that they have collected from you. This “Right to Delete” is subject to certain
            exceptions as provided by law.
          </Text>
          <br />
          <br />
          <Text weight="bold">Blend does not sell your data</Text>
          <br />
          <br />
          <Text>
            While the CCPA provides California residents with the right to opt out of the “sale” of their personal
            information, Blend has not “sold” consumer personal information in the preceding 12 months and does not
            “sell” your personal information as we understand that term to be defined by the California Consumer Privacy
            Act and its implementing regulations.
          </Text>
          <br />
          <br />
          <Text weight="bold">Authorized Agents</Text>
          <br />
          <br />
          <Text>
            When a consumer uses an authorized agent to submit a request to know or a request to delete, a business may
            require that the consumer do the following (this does not apply when a consumer has provided the authorized
            agent with power of attorney pursuant to Probate Code sections 4000 to 4465):
          </Text>
          <br />
          <ul>
            <Li centerAligned={false}> Provide the authorized agent signed permission to do so.</Li>
            <Li centerAligned={false}> Verify their own identity directly with the business.</Li>
            <Li centerAligned={false}>
              Directly confirm with the business that they provided the authorized agent permission to submit the
              request.
            </Li>
          </ul>
          <br />
          <Text>
            A business may deny a request from an authorized agent that does not submit proof that they have been
            authorized by the consumer to act on their behalf.
          </Text>
          <br />
          <br />
          <Text weight="bold">Additional Rights</Text>
          <br />
          <br />
          <Text>
            Under California Civil Code Sections 1798.83-1798.84, California residents are entitled to ask certain
            covered businesses for a notice (a) identifying the categories of personal information (as defined by
            California law) that are shared with third parties for their own marketing purposes and (b) providing
            contact information for such third parties. However, as disclosed here in our Privacy Notice, Blend does not
            share such personal information for these purposes.
          </Text>
          <br />
          <br />
          <Text weight="bold">Financial Incentives</Text>
          <br />
          <br />
          <Text>
            We do not offer financial incentives for the collection of your data. If this changes, we will notify you
            prior to doing so, and we will obtain your consent to opt-in to this program before enrolling you.
          </Text>
          <br />

          <br />
          <Text weight="bold">Non-discrimination Rights</Text>
          <br />
          <br />
          <Text>
            You also have the right to not be discriminated against for exercising certain of your rights under the
            CCPA. If you exercise your rights under the CCPA, we will not:
          </Text>
          <ul>
            <Li centerAligned={false}>Deny you goods or services.</Li>
            <Li centerAligned={false}>
              Charge you different prices or rates for goods or services, including through granting discounts or other
              benefits, or imposing penalties.
            </Li>
            <Li centerAligned={false}>Provide you a different level or quality of goods or services.</Li>
            <Li centerAligned={false}>
              Suggest that you may receive a different price or rate for goods or services or a different level or
              quality of goods or services.
            </Li>
          </ul>
          <br />
          <br />
          <Text weight="bold">Contact us</Text>
          <br />
          <br />
          <Text>
            If you have any questions regarding your rights under California law or would like to make a request based
            on your rights indicated above, you may reach out to us at privacy@blend.com.
          </Text>
          <br />
          <br />
          <Text>
            In order to process your request to know/access or delete personal information we collect, disclose, or
            sell, we must verify your request. We verify your identity by asking you to provide personal identifiers and
            a form of identification so that we can match against information we may have collected from you previously
            and confirm your request using the email or telephone account stated in the request. We will not use
            personal information we collect in connection with verifying or responding to your request for any purpose
            other than responding to your request.
          </Text>
          <br />
          <br />
          <br />
          <Header size={1}>7. Nevada Residents</Header>
          <br />
          <Text>
            Under Nevada law, certain Nevada consumers may opt out of the sale of certain pieces of their information to
            other companies who will sell or license their information to others. We do not engage in sales subject to
            this law; however, if you are a Nevada resident, you may submit a request to opt out of any potential future
            sales under Nevada law by emailing privacy@blend.com. Please note we will take reasonable steps to verify
            your identity and the authenticity of the request. Once verified, we will maintain your request in the event
            our practices change.
          </Text>
          <br />
          <br />
          <br />
          <Header size={1}>8. Security</Header>
          <br />
          <Text>
            We have implemented administrative, technical, and physical security measures to protect against the loss,
            misuse, and/or alteration of your information. These safeguards vary based on the sensitivity of the
            information that we collect and store. However, we cannot and do not guarantee that these measures will
            prevent every unauthorized attempt to access, use, or disclose your information since despite our efforts,
            no Internet and/or other electronic transmissions can be completely secure.
          </Text>
          <br />
          <br />
          <br />
          <Header size={1}>9. Data Retention</Header>
          <br />
          <Text>
            We will hold your information for as long as necessary to fulfill the purposes set forth in this Privacy
            Notice or as long as we are legally required or permitted to do so.
          </Text>
          <br />
          <br />
          <br />
          <Header size={1}>10. Third-Party Links and Services</Header>
          <br />
          <Text>
            The Services may contain links to third-party websites, applications, and other services. Please be aware
            that we are not responsible for the privacy practices of such other sites and services. If you choose to use
            such third-party links and services, we encourage you to read the privacy statements of each and every site
            you visit.
          </Text>
          <br />

          <br />
          <br />
          <Header size={1}>11. Changes to this Privacy Notice</Header>
          <br />
          <Text>
            We will continue to evaluate this Privacy Notice as we update and expand our Services, and we may make
            changes to the Privacy Notice accordingly. Any changes will be posted here and you should check this page
            periodically for updates. If we make material changes to this Privacy Notice, we may provide you with notice
            if required by law.
          </Text>
          <br />

          <br />
          <br />
          <Header size={1}>12. Questions About this Privacy Notice</Header>
          <br />
          <Text>
            If you have any questions about this Privacy Notice or our privacy practices, you can contact us using the
            following information:
            <br />
            Blend Labs, Inc.
            <br />
            415 Kearny Street
            <br />
            San Francisco, CA 94108 <br />
            Tel: (650) 550-4810
            <br />
            Email: privacy@blend.com
            <br />
          </Text>
          <br />
        </div>
        <div className={css(styles.footer)}>
          <ActionButton id="it-PrivacyPolicy-accept" label="Done" onClick={handleClose} theme="primary" />
        </div>
      </ModalContent>
    </Modal>
  );
};
